import React from 'react';
import styled from 'styled-components';
import { connect } from 'react-redux';
import { CallHistoryMethodAction, push } from 'connected-react-router';
import { Dispatch } from 'redux';
import AppState from '@webapp/store/state';
import MotionOpacity from '@components/motion/motion-opacity';
import { Flex } from '@components/layout/flex';
import SimpleArtist from '@common/types/artist/simple-artist';
import { Translate } from 'react-localize-redux';
import {
  PublishRelease,
  UpdateField,
} from '@webapp/store/release/action-creators';
import { Order } from '@webapp/types/orders/order';
import { Release } from '@common/types/release/release';
import { releaseCheckout } from '@webapp/store/release/selectors';

interface ReleasePaymentConfirmationPageProps {
  className?: string;
  artist?: SimpleArtist;
  release?: Partial<Release>;
  basket: Order[];
  isPro?: boolean;
  updatePaidItems: (basket: Order[]) => UpdateField<'paidItems'>;
  publish: (
    artistId: string,
    releaseId: string,
    release: Partial<Release>
  ) => PublishRelease;
  goTo: (url: string) => CallHistoryMethodAction;
}

const mapStateToProps = (state: AppState) => ({
  basket: releaseCheckout(state),
  artist: state.artist.active,
  release: state.release.release,
  isPro: state.subscription.current?.plan?.includes('Pro'),
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  goTo: (url: string) => dispatch(push(url)),
  publish: (artistId: string, releaseId: string, release: Partial<Release>) =>
    dispatch(new PublishRelease(artistId, releaseId, release)),
  updatePaidItems: (basket: Order[]) =>
    dispatch(new UpdateField<'paidItems'>('paidItems', basket, true)),
});

const PaymentSuccess = (props: ReleasePaymentConfirmationPageProps) => {
  const { basket, artist, release, isPro } = props;
  const [published, setPublished] = React.useState(false);
  const queryParams = new URLSearchParams(window.location.search);
  const stripeStatus = queryParams.get('redirect_status');
  const paymentIntent = queryParams.get('payment_intent');

  React.useEffect(() => {
    if (isPro && artist && release && !published) {
      props.publish(artist.id, release.id as string, {
        ...release,
      });

      setPublished(true);
    }
    if (
      stripeStatus === 'succeeded' &&
      artist &&
      release &&
      paymentIntent &&
      !published
    ) {
      const newBasket = (release?.paidItems || []).concat(
        basket.map((item) => ({
          ...item,
          source: 'stripe',
          date: Number(new Date()),
          transactionId: paymentIntent,
        }))
      );

      props.updatePaidItems(newBasket);

      props.publish(artist.id, release.id as string, {
        ...release,
        paidItems: newBasket,
      });

      setPublished(true);
    }
  }, [
    stripeStatus,
    published,
    paymentIntent,
    props,
    artist,
    basket,
    release,
    isPro,
  ]);

  return (
    <Container>
      <ConfirmationWrapper className={props.className}>
        <Title>
          <Translate id="STRIPE.PAYMENT.CONFIRMATION.TITLE" />
        </Title>
      </ConfirmationWrapper>
      <RedirectingContainer>
        <p className={'redirecting'}>
          <Translate id="STRIPE.PAYMENT.CONFIRMATION.REDIRECT_TITLE" />
        </p>
        <a href={'/catalog'}>
          <Translate id="STRIPE.PAYMENT.CONFIRMATION.REDIRECT_TEXT" />
        </a>
      </RedirectingContainer>
    </Container>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(PaymentSuccess);

const RedirectingContainer = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  color: ${({ theme }) => theme.colors.primary};
  transform: translate(-50%, -50%);
  display: flex;
  max-width: 300px;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  a,
  p {
    font-family: 'Sen', sans-serif;
    font-size: 14px;
    line-height: 18px;
    text-decoration: underline;
  }
  .redirecting {
    text-transform: uppercase;
    font-weight: 700;
    max-width: 180px;
    text-decoration: none;
  }
  ${({ theme }) =>
    `@media screen and (min-width: ${theme.breakpoints.tablet}px)`} {
    top: 44%;
    max-width: 400px;
  }
  ${({ theme }) =>
    `@media screen and (min-width: ${theme.breakpoints.laptopS}px)`} {
    top: 80%;
  }
`;

const Container = styled(MotionOpacity)`
  position: relative;
  margin-top: 100px;
`;

const ConfirmationWrapper = styled(Flex)`
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  max-width: 340px;
  height: 220px;
  position: absolute;
  top: 30%;
  left: 50%;
  transform: translate(-50%, -50%);
  background-color: ${({ theme }) => theme.colors.white};
  padding: 80px 30px 20px;
  border-radius: 4px;
  ${({ theme }) =>
    `@media screen and (min-width: ${theme.breakpoints.tablet}px)`} {
    max-width: 420px;
    top: 30%;
  }
  ${({ theme }) =>
    `@media screen and (min-width: ${theme.breakpoints.laptopS}px)`} {
    top: 50%;
  }
`;

const Title = styled.h4`
  color: ${({ theme }) => theme.colors.primary};
  font-size: 24px;
  font-weight: 400;
  line-height: 30px;
  margin: 0;
  text-align: left;
`;
